import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AuthorItem, ItemSummary, ItemFilters, PaginationParams, PaginatedResponse } from '../../types/item';
import { itemService } from '../../services/itemService';

interface ItemsState {
  // List view
  items: ItemSummary[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  filters: ItemFilters;
  isLoading: boolean;
  error: string | null;
  
  // Editor view
  currentItem: AuthorItem | null;
  isEditing: boolean;
  isSaving: boolean;
  saveError: string | null;
  
  // Expanded rows in table
  expandedRows: string[];
}

const initialState: ItemsState = {
  items: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 30,
  totalPages: 0,
  filters: {},
  isLoading: false,
  error: null,
  
  currentItem: null,
  isEditing: false,
  isSaving: false,
  saveError: null,
  
  expandedRows: [],
};

// Async thunks
export const fetchItems = createAsyncThunk(
  'items/fetchItems',
  async (params: { filters: ItemFilters; pagination: PaginationParams }, { rejectWithValue }) => {
    try {
      const response = await itemService.getItems(params.filters, params.pagination);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch items');
    }
  }
);

export const fetchItemById = createAsyncThunk(
  'items/fetchItemById',
  async (id: string, { rejectWithValue }) => {
    try {
      const item = await itemService.getItemById(id);
      return item;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch item');
    }
  }
);

export const saveItem = createAsyncThunk(
  'items/saveItem',
  async (item: Partial<AuthorItem>, { rejectWithValue }) => {
    try {
      if (item.id) {
        return await itemService.updateItem(item.id, item);
      } else {
        return await itemService.createItem(item as Omit<AuthorItem, 'id'>);
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to save item');
    }
  }
);

export const deleteItem = createAsyncThunk(
  'items/deleteItem',
  async (id: string, { rejectWithValue }) => {
    try {
      await itemService.deleteItem(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete item');
    }
  }
);

const itemsSlice = createSlice({
  name: 'items',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<ItemFilters>) => {
      state.filters = action.payload;
      state.currentPage = 1; // Reset to first page when filters change
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1; // Reset to first page when page size changes
    },
    toggleExpandedRow: (state, action: PayloadAction<string>) => {
      const rowId = action.payload;
      const index = state.expandedRows.indexOf(rowId);
      if (index !== -1) {
        state.expandedRows.splice(index, 1);
      } else {
        state.expandedRows.push(rowId);
      }
    },
    clearExpandedRows: (state) => {
      state.expandedRows = [];
    },
    setCurrentItem: (state, action: PayloadAction<AuthorItem | null>) => {
      state.currentItem = action.payload;
      state.isEditing = !!action.payload;
    },
    updateCurrentItem: (state, action: PayloadAction<Partial<AuthorItem>>) => {
      if (state.currentItem) {
        state.currentItem = { ...state.currentItem, ...action.payload };
      }
    },
    clearCurrentItem: (state) => {
      state.currentItem = null;
      state.isEditing = false;
      state.saveError = null;
    },
    clearError: (state) => {
      state.error = null;
      state.saveError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch items
      .addCase(fetchItems.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchItems.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload.items;
        state.totalCount = action.payload.totalCount;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.page;
        state.pageSize = action.payload.pageSize;
      })
      .addCase(fetchItems.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch item by ID
      .addCase(fetchItemById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchItemById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentItem = action.payload;
        state.isEditing = true;
      })
      .addCase(fetchItemById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Save item
      .addCase(saveItem.pending, (state) => {
        state.isSaving = true;
        state.saveError = null;
      })
      .addCase(saveItem.fulfilled, (state, action) => {
        state.isSaving = false;
        state.currentItem = action.payload;
        // Update the item in the list if it exists
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          // Convert full item to summary for the list
          state.items[index] = {
            id: action.payload.id,
            questionPreview: action.payload.question.substring(0, 100),
            subject: action.payload.taxonomy.subject,
            grades: action.payload.taxonomy.grades,
            bloomLevel: action.payload.taxonomy.bloomLevel,
            difficulty: action.payload.difficulty,
            tags: action.payload.tags,
            status: action.payload.status,
            createdAt: action.payload.createdAt,
            updatedAt: action.payload.updatedAt,
            createdBy: action.payload.createdBy,
          };
        }
      })
      .addCase(saveItem.rejected, (state, action) => {
        state.isSaving = false;
        state.saveError = action.payload as string;
      })
      // Delete item
      .addCase(deleteItem.fulfilled, (state, action) => {
        state.items = state.items.filter(item => item.id !== action.payload);
        state.totalCount -= 1;
      });
  },
});

export const {
  setFilters,
  setPage,
  setPageSize,
  toggleExpandedRow,
  clearExpandedRows,
  setCurrentItem,
  updateCurrentItem,
  clearCurrentItem,
  clearError,
} = itemsSlice.actions;

export default itemsSlice.reducer;
